<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <h2 class="title-subsection text-ink">Admin</h2>
                <BaseButton theme="neutral" size="sm" class="ml-auto" @click="emit('settings')">
                    <i class="bi bi-gear" aria-hidden="true"></i>
                    Settings
                </BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="templates.length === 0" @click="emit('new-campaign', templates)">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    New campaign
                </BaseButton>
                <BaseButton theme="neutral" size="sm" @click="emit('drafts')">
                    <i class="bi bi-pencil-square" aria-hidden="true"></i>
                    Drafts
                    <BaseCountBadge v-if="draftCount > 0" :count="draftCount" />
                </BaseButton>
            </div>
            <p class="body-small text-ink-muted">Organization-wide campaign insights and reporting.</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <div v-if="loading" class="body-small text-ink-muted">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else>
            <AdminCompletionGlance :completion="completion" />

            <div class="flex flex-col gap-2">
                <div class="scaffold-details-small text-ink-muted">{{ t("reflectionsAdmin.hub.activeCampaigns") }}</div>
                <BaseCard v-if="activeCampaigns.length === 0" padding="md" class="body-small text-ink-muted">
                    No active campaigns. Create one to roll out an assessment to everyone in your organization.
                </BaseCard>
                <AdminCampaignRow
                    v-for="campaign in activeCampaigns"
                    :key="campaign.id"
                    :campaign="campaign"
                    :busy="transitioningId === campaign.id"
                    :expanded="expandedCampaignId === campaign.id"
                    :hide-rater-status="hideRaterStatus"
                    @close="onClose"
                    @view="onView"
                    @toggle="onToggle" />
            </div>

            <div v-if="closedCampaigns.length" class="flex flex-col gap-2 mt-5">
                <div class="scaffold-details-small text-ink-muted">Previous Reflections</div>
                <AdminCampaignRow
                    v-for="campaign in closedCampaigns"
                    :key="campaign.id"
                    :campaign="campaign"
                    :busy="false"
                    :expanded="expandedCampaignId === campaign.id"
                    :hide-rater-status="hideRaterStatus"
                    @view="onView"
                    @toggle="onToggle" />
            </div>

            <BaseNotification v-if="actionStatus" :theme="actionStatus.theme" :text="actionStatus.text" @dismiss="actionStatus = null" />
        </template>

        <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, AdminCompletionSummary, AdminHubPayload, TemplateSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import AdminCampaignRow from "~vue/pages/reflections/AdminCampaignRow.vue";
import AdminCompletionGlance from "~vue/pages/reflections/AdminCompletionGlance.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const campaigns = ref<AdminCampaignSummary[]>([]);
const templates = ref<TemplateSummary[]>([]);
const completion = ref<AdminCompletionSummary | null>(null);
const hideRaterStatus = ref(false);
// One campaign open at a time; the panel loads its rows lazily on expand.
const expandedCampaignId = ref<string | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const transitioningId = ref<string | null>(null);
const actionStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);

const emit = defineEmits<{
    (event: "new-campaign", templates: TemplateSummary[]): void;
    (event: "view-campaign", campaignId: string): void;
    (event: "settings"): void;
    (event: "drafts"): void;
}>();

// Drafts live on their own subpage; this list is what participants can see.
const activeCampaigns = computed(() => campaigns.value.filter((c) => c.state === "active"));
const draftCount = computed(() => campaigns.value.filter((c) => c.state === "draft").length);
const closedCampaigns = computed(() => campaigns.value.filter((c) => c.state === "closed"));
const campaignDemoActions = computed(() => activeCampaigns.value.map((campaign) => ({ id: campaign.id, label: campaign.name })));

async function loadHub() {
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns").json<AdminHubPayload>();
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load campaigns.";
        return;
    }
    loadError.value = null;
    campaigns.value = data.value?.campaigns ?? [];
    templates.value = data.value?.templates ?? [];
    completion.value = data.value?.completion ?? null;
    hideRaterStatus.value = data.value?.hide_rater_status ?? false;
}

onMounted(async () => {
    await loadHub();
    loading.value = false;
});

function onView(campaignId: string) {
    emit("view-campaign", campaignId);
}

function onToggle(campaignId: string) {
    expandedCampaignId.value = expandedCampaignId.value === campaignId ? null : campaignId;
}

async function onClose(campaign: AdminCampaignSummary) {
    if (!globalThis.confirm(`Close "${campaign.name}"? This also closes all participant cycles and can't be undone.`)) {
        return;
    }
    transitioningId.value = campaign.id;
    actionStatus.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/close`, { method: "POST" }).json<AdminCampaignResponse>();
    transitioningId.value = null;
    if (fetchError.value) {
        actionStatus.value = { theme: "error", text: (data.value as { error?: string } | null)?.error || "Could not close the campaign." };
        return;
    }
    if (data.value) {
        campaigns.value = campaigns.value.map((existing) => (existing.id === campaign.id ? (data.value as AdminCampaignResponse).campaign : existing));
    }
    actionStatus.value = { theme: "success", text: `Closed "${campaign.name}".` };
    // Closing changes what is in flight, so the completion summary is stale
    // until it is re-read. Refetched without the loading flag so the panel
    // stays on screen instead of blanking to the spinner.
    await loadHub();
}
</script>
